import type { JobAsset, JobAssetDto, JobStatus } from './job.types';

/**
 * Asset 域型別 — signed download URL + per-project listing。
 * -----------------------------------------------------------------------------
 * 對應 `assetsService`(第一公民 service):
 *   - signed URL:`GET /projects/{pid}/jobs/{jid}/assets/{aid}/download-url`(R28)
 *   - per-project listing:`GET /projects/{pid}/assets`(R29;cursor 分頁 / nodeId filter)
 *
 * 原寄居在 `job.types.ts`;`AssetWithJobContext(Dto)` 仍以 `JobAsset(Dto)` 為 base
 * (per-asset attach 來源 job context),耦合靠 import `./job.types` 保留。mapper 在
 * `assets.mappers.ts`。
 */

/**
 * Asset signed download URL DTO。BE 用 GCS V4 sign 簽 15min TTL(短於 uploads 的 60min);
 * `mime_type` 對齊 4 種 `AssetKind` derived。
 */
export interface AssetDownloadUrlDto {
  download_url: string;
  download_url_expires_datetime: string;
  mime_type: string;
}

/**
 * Asset signed download URL(camelCase 業務層用)。`downloadUrl` 是 GCS signed URL,
 * 預設 15min TTL;過期後重打 `getAssetDownloadUrl` 取新,或用 `<img onError={refetch}>`
 * 處理過期。
 */
export interface AssetDownloadUrl {
  downloadUrl: string;
  downloadUrlExpiresDatetime: string;
  mimeType: string;
}

/**
 * T10(docs/50)— asset ABCD 評分投影(FE-safe;BE 已去掉 BQ 內部表名)。
 *
 * BE 端是 **opaque dict**(刻意鬆散,避免 abcds 上游 feature schema 變動拖累 BE
 * release),只 `status` 穩定。`summary.combined.{detected_count,feature_total,
 * adherence_percent,by_dimension}` 給 M-04 badge 派生 / M-05 結果頁;FE 讀取一律
 * 防御(欄位可能缺)。`null` = 未評 / 非 VIDEO / 失敗。
 */
export interface AssetAbcdProjectionDto {
  /** `SUCCEEDED` / `FAILED` / `SKIPPED_INCOMPLETE_BRAND_METADATA` / `RUNNING` / `PENDING` … */
  status: string;
  feature_count?: number;
  summary?: Record<string, unknown>;
  cost_credits?: number;
  evaluated_at?: string;
  error?: string;
  error_class?: string;
  /** opaque — 容忍 BE 端加欄不破 mapper。 */
  [k: string]: unknown;
}

/** :type:`AssetAbcdProjectionDto` 的 camelCase 業務型別(known 欄位 normalize)。 */
export interface AssetAbcdProjection {
  status: string;
  featureCount?: number;
  /** opaque(summary.combined.* 等);M-04 badge / M-05 防御讀取。 */
  summary?: Record<string, unknown>;
  costCredits?: number;
  evaluatedAt?: string;
  error?: string;
  errorClass?: string;
}

/**
 * Asset listing(per-project)DTO。每筆 asset attach 來源 job context(jobId /
 * overall_status / submitted_datetime),給 UI 顯示「來自 Job <X> @ <date> [SUCCEEDED]」用。
 */
export interface AssetWithJobContextDto extends JobAssetDto {
  job_id: string;
  job_overall_status: JobStatus;
  job_submitted_datetime: string;
  /** T10 — ABCD 評分投影;BE 省略 / null = 未評。 */
  abcd?: AssetAbcdProjectionDto | null;
}

export interface AssetListResponseDto {
  assets: AssetWithJobContextDto[];
  next_cursor: string | null;
}

/** Asset listing(per-project,camelCase 業務層用)。 */
export interface AssetWithJobContext extends JobAsset {
  jobId: string;
  jobOverallStatus: JobStatus;
  jobSubmittedDatetime: string;
  /** T10 — ABCD 評分投影;`null` = 未評 / 非 VIDEO / 失敗。 */
  abcd: AssetAbcdProjection | null;
}

export interface AssetListResponse {
  assets: AssetWithJobContext[];
  nextCursor: string | null;
}

// ─── R199 asset 排序時間鍵(ULID fallback)────────────────────────────────────
//
// **為什麼需要**:`created_datetime` 在 job 還 RUNNING 時可能是 null(R59 已知),而
// `version` 幫不上忙 —— BE `AssetEntity.version` 是 `default=1` 的固定欄位
// (`api-service/app/workflow/models.py:627`),同一顆 node 跨兩個 job 產的 asset 都是 1。
// 兩個鍵都打平 → `Array.sort` 穩定 → 順序退化成「進 store 的先後」→ 舊 job 的 asset 反而
// 排第一,被標成「最新版」(Bryan 2026-08-03 實測)。
//
// **解法**:asset id / job id 都是 BE 產的 ULID,**前 10 字就是 48-bit 毫秒時間戳**
// (Crockford base32,單調遞增)→ 沒有 `created_datetime` 也永遠有時間可排。

/** Crockford base32 字母表(ULID 規範:排除 I / L / O / U)。 */
const CROCKFORD32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** ULID 時間段 = 前 10 字,且必須全在 Crockford 字母表內。 */
const ULID_TIME_HEAD = /^[0-9A-HJKMNP-TV-Z]{10}/;

/**
 * 解 ULID 前 10 字的毫秒時間戳。
 *
 * @param id 期望是 26 字 ULID(BE 的 asset_id / job_id);非 ULID(uuid / 測試 fixture
 *   的 `'a1'`)回 `null`,caller 自行退下一個來源。
 * @returns epoch ms,或 `null`(不是 ULID)。
 *
 * @example
 *   decodeUlidTimeMs('01KZ3GXMTJG5A438XP94HMJY77'); // → 1786... (產生當下的 ms)
 *   decodeUlidTimeMs('a1');                          // → null
 */
export function decodeUlidTimeMs(id: string | null | undefined): number | null {
  if (typeof id !== 'string' || id.length < 10) return null;
  const head = id.slice(0, 10).toUpperCase();
  if (!ULID_TIME_HEAD.test(head)) return null;
  let ms = 0;
  for (const ch of head) {
    const v = CROCKFORD32.indexOf(ch);
    if (v < 0) return null;
    ms = ms * 32 + v;
  }
  return ms;
}

/**
 * Asset 排序用的時間鍵(epoch ms),來源優先序:
 *   1. `createdDatetime`(BE 寫的產出時間 — 語意最準)
 *   2. `assetId` 的 ULID 時間段(RUNNING job 尚未填 createdDatetime 時的真相)
 *   3. `jobId` 的 ULID 時間段(asset id 不是 ULID 的防禦層)
 *
 * 三者皆無 → `null`;caller 沿 R59 慣例把 `null` 當「最新」(不可當 0,否則剛產出的
 * asset 會被 LRU cap 誤 evict)。
 */
export function assetSortTimeMs(
  asset: Pick<AssetWithJobContext, 'createdDatetime' | 'assetId' | 'jobId'>,
): number | null {
  const parsed = asset.createdDatetime ? Date.parse(asset.createdDatetime) : Number.NaN;
  if (Number.isFinite(parsed)) return parsed;
  return decodeUlidTimeMs(asset.assetId) ?? decodeUlidTimeMs(asset.jobId) ?? null;
}

// ─── R372 畫布素材池(ProjectAsset;docs/54 §P-82 / BE docs/127)─────────────────
//
// 「畫布素材 vs 平台素材庫」scope 分離的**畫布側**資料:`projects/{pid}/assets` 的
// ProjectAsset 集合。⚠️ 與上面 `AssetWithJobContext`(`GET /projects/{pid}/assets`,
// `jobs/*/output_assets` collection group = 生成產物 × job context)是**兩個不同集合**:
// 本池 = 上傳登記(source=upload)+ 素材庫匯入複製(library_import)+ 產物 promotion
// (generated),endpoint prefix 刻意是 `/project_assets`。

/** 畫布素材池單筆 DTO(pa.1 `items[]` / pa.2 result 同形;pa.2 另多 `already_existed`)。 */
export interface CanvasPoolAssetDto {
  asset_id: string;
  asset_kind: string;
  source: string;
  source_library_item_id?: string | null;
  source_library_id?: string | null;
  gcs_uri?: string | null;
  /** FE convention 欄(subject_kind/subject_id/view/name…)+ server 推欄(mime/size…)。 */
  metadata?: Record<string, unknown> | null;
  used_in_node_ids?: string[] | null;
  created_by_cuid?: string | null;
  created_at?: string | null;
}

/**
 * 畫布素材池單筆(camelCase)。`assetKind` / `source` 保持開放 string(wire 值域開放,
 * R296:封閉 union 是型別謊報);消費端 filter 已知值即可。
 */
export interface CanvasPoolAsset {
  assetId: string;
  assetKind: string;
  source: string;
  sourceLibraryItemId: string | null;
  sourceLibraryId: string | null;
  gcsUri: string | null;
  metadata: Record<string, unknown>;
  usedInNodeIds: string[];
  createdAt: string | null;
}

export interface CanvasPoolListResponseDto {
  items: CanvasPoolAssetDto[];
  next_cursor?: string | null;
}

export interface CanvasPoolListResponse {
  items: CanvasPoolAsset[];
  nextCursor: string | null;
}

/** pa.2 登記回包(池單筆 + `already_existed`:doc 先前已在 = 綁卡先 promote / 重複登記)。 */
export interface CanvasPoolRegisterResultDto extends CanvasPoolAssetDto {
  already_existed?: boolean;
}

export interface CanvasPoolRegisterResult {
  asset: CanvasPoolAsset;
  alreadyExisted: boolean;
}
